import { useMemo, useState } from "react";
import dayjs from "dayjs";
import axios from "../api/axios";
import { usePending } from "../context/PendingContext";

const initialItems = [{ product_catalog_id: "", product_id: "", amount: "" }];

export function useTransferForm({ rows, products, fetchSites }) {
  const { refreshPending } = usePending();
  const userId = Number(localStorage.getItem("user_id") || "");
  const [open, setOpen] = useState(false);

  const [transferForm, setTransferForm] = useState({
    transfer_type: "",
    from_site_id: "",
    to_site_id: "",
    supplier: "",
    expected_date: dayjs(),
    remark: "",
    selectedProductCatalog: null,
    receiver: userId,
    pair_up_code: "",
    items: initialItems,
  });

  const [originalInventorySummary, setOriginalInventorySummary] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // --------------------------
  // SITE OPTIONS
  // --------------------------
  const siteOptions = useMemo(
    () => rows.filter((s) => s.site_status !== "delivered"),
    [rows]
  );

  const fromSiteOptionsFiltered = useMemo(
    () =>
      siteOptions.filter(
        (s) => String(s.id) !== String(transferForm.to_site_id || "")
      ),
    [siteOptions, transferForm.to_site_id]
  );

  const toSiteOptionsFiltered = useMemo(
    () =>
      siteOptions.filter(
        (s) => String(s.id) !== String(transferForm.from_site_id || "")
      ),
    [siteOptions, transferForm.from_site_id]
  );

  // --------------------------
  // OPEN / CLOSE
  // --------------------------
  const openDialog = (transferType) => {
    setTransferForm({
      transfer_type: transferType,
      from_site_id: "",
      to_site_id: "",
      supplier: "",
      expected_date: dayjs(),
      remark: "",
      selectedProductCatalog: null,
      receiver: userId,
      pair_up_code: "",
      items: initialItems,
    });
    setOriginalInventorySummary([]);
    setError("");
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  // --------------------------
  // HELPERS
  // --------------------------
  const buildCatalogsFromInventory = (siteId) => {
    const site = siteOptions.find((s) => String(s.id) === String(siteId));
    const inv = site?.inventory_summary || [];
    const byCat = new Map();

    inv.forEach((i) => {
      if (!byCat.has(i.product_catalog_id)) {
        byCat.set(i.product_catalog_id, {
          id: i.product_catalog_id,
          name: i.product_catalog_name,
          products: [],
        });
      }
      if (Number(i.amount) !== 0) {
        byCat.get(i.product_catalog_id).products.push({
          id: i.product_id,
          name: i.product_name,
        });
      }
    });

    return Array.from(byCat.values());
  };

  const getProductCatalogOptions = () => {
    if (transferForm.transfer_type === "transfer") {
      if (!transferForm.from_site_id) return [];
      return buildCatalogsFromInventory(transferForm.from_site_id);
    }
    return products || [];
  };

  const getProductOptions = (catalogId) => {
    if (!catalogId) return [];
    if (transferForm.transfer_type === "transfer") {
      const cats = getProductCatalogOptions();
      const cat = cats.find((c) => String(c.id) === String(catalogId));
      const list = cat?.products || [];
      return list.map((p) => ({ product_id: p.id, product_name: p.name }));
    }
    const cat = (products || []).find((c) => String(c.id) === String(catalogId));
    const list = cat?.product_group || cat?.products || [];
    return list.map((p) => ({ product_id: p.id, product_name: p.name }));
  };

  // --------------------------
  // SITE SELECT CHANGE
  // --------------------------
  const handleSiteChange = (key, value) => {
    const selectedSite = rows.find((site) => site.id === parseInt(value, 10));

    // Cases where we auto-fill items from inventory_summary
    const shouldPrefillInventory =
      (transferForm.transfer_type === "finished_project" && key === "to_site_id") ||
      (transferForm.transfer_type === "transfer" && key === "from_site_id") ||
      (transferForm.transfer_type === "product_sales" && key === "from_site_id") ||
      (transferForm.transfer_type === "daily_operation" && key === "to_site_id");
    if (shouldPrefillInventory) {
      const summary = selectedSite?.inventory_summary || [];
      setOriginalInventorySummary(summary);

      const items = summary.map((item) => ({
        product_catalog_id: item.product_catalog_id,
        product_id: item.product_id,
        amount: "",
        amount: transferForm.transfer_type === "finished_project" ? (Number(item.amount) || 0) : "",
        max_amount: Number(item.amount) || 0,
        price: "",
      }));

      setTransferForm((prev) => ({
        ...prev,
        [key]: value,
        items,
        ...(key === "from_site_id" ? { selectedProductCatalog: null } : {}),
      }));
    } else {
      if ((transferForm.transfer_type === "transfer" && key === "to_site_id") ||
    (transferForm.transfer_type === "product_sales" && key === "to_site_id")) {
        setTransferForm((prev) => ({
          ...prev,
          [key]: value,
        }));
      } else {
        setTransferForm((prev) => ({
          ...prev,
          [key]: value,
          items: initialItems,
          ...(key === "from_site_id" ? { selectedProductCatalog: null } : {}),
        }));
      }
    }
  };

  // --------------------------
  // TRANSFER ITEMS
  // --------------------------
  const addTransferItem = () => {
    setTransferForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { product_catalog_id: "", product_id: "", amount: "", max_amount: "", price: "", },
      ],
    }));
  };

  const removeTransferItem = (index) => {
    setTransferForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // const updateTransferItem = (index, key, value) => {
  //   const isClampedType =
  //     transferForm.transfer_type === "daily_operation" ||
  //     transferForm.transfer_type === "transfer";

  //   const updatedItems = [...transferForm.items];
  //   const row = { ...updatedItems[index] };

  //   if (key === "amount" && isClampedType) {
  //     if (value === "" || value === null) {
  //       row.amount = "";
  //     } else {
  //       const num = Number(value);
  //       const max = Number(row.max_amount ?? 0);
  //       const clamped = Math.max(
  //         0,
  //         Math.min(Number.isFinite(num) ? num : 0, max)
  //       );
  //       row.amount = clamped;
  //     }
  //   } else {
  //     row[key] = value;
  //   }

  //   updatedItems[index] = row;
  //   setTransferForm((prev) => ({ ...prev, items: updatedItems }));
  // };

  const updateTransferItem = (index, key, value) => {
    setTransferForm((prev) => {
      const isClampedType =
        prev.transfer_type === "daily_operation" ||
        prev.transfer_type === "transfer";

      const items = [...prev.items];
      const row = { ...items[index] };

      if (key === "amount" && isClampedType) {
        if (value === "" || value === null) {
          row.amount = "";
        } else {
          const num = Number(value);
          const max = Number(row.max_amount ?? 0);
          const clamped = Math.max(
            0,
            Math.min(Number.isFinite(num) ? num : 0, max)
          );
          row.amount = clamped;
        }
      } else {
        row[key] = value;
      }

      items[index] = row;
      return { ...prev, items };
    });
  };


  // --------------------------
  // FLAGS FOR UI
  // --------------------------
  const showToSite = !!transferForm.transfer_type;
  const showFromSite = ["transfer"].includes(transferForm.transfer_type);
  const showRestFields =
    showToSite &&
    (!showFromSite || transferForm.from_site_id) &&
    transferForm.to_site_id;

  const renderDateLabel = (transferType) => {
    if (transferType === "supplier") return "預計到貨日期";
    if (transferType === "daily_operation") return "消耗日期";
    if (transferType === "finished_project") return "點貨日期";
    if (transferType === "transfer") return "轉貨日期";
    if (transferType === "product_sales") return "賣貨日期";
    return "日期";
  };

  // --------------------------
  // SUBMIT TRANSFER
  // --------------------------
  const validateTransfer = () => {
    // You can add validation here if needed.
    return "";
  };

  const submitTransfer = async () => {
    const v = validateTransfer();
    if (v) {
      setError(v);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const isNegative = ["daily_operation", "resell"].includes(
        transferForm.transfer_type
      );

      let items = transferForm.items.filter(
        (item) => item.product_id && item.amount
      );

      let payload = {
        ...transferForm,
        expected_date: transferForm.expected_date.format("YYYY-MM-DD"),
        items: items.map((item) => ({
          ...item,
          amount: isNegative
            ? -Math.abs(Number(item.amount))
            : Number(item.amount),
        })),
        receiver: transferForm.receiver,
        pair_up_code: transferForm.pair_up_code || "",
      };

      if (transferForm.transfer_type === "finished_project") {
        const originalMap = Object.fromEntries(
          originalInventorySummary.map((item) => [
            item.product_id,
            Number(item.amount),
          ])
        );
        const editedMap = Object.fromEntries(
          transferForm.items
            .filter((item) => item.product_id)
            .map((item) => [item.product_id, Number(item.amount)])
        );

        const allProductIds = new Set([
          ...Object.keys(originalMap),
          ...Object.keys(editedMap),
        ]);

        items = Array.from(allProductIds)
          .map((product_id) => {
            const originalAmount = originalMap[product_id] || 0;
            const editedAmount = editedMap[product_id] || 0;
            return {
              product_id,
              amount: editedAmount - originalAmount,
            };
          })
          .filter((item) => item.amount !== 0);

        payload = { ...transferForm, items };
      }

      // if (transferForm.transfer_type === "daily_operation") {
      //   await axios.post("/directTransfer", payload);
      // } else if (payload?.items?.length) {
      await axios.post("/transfers", payload);
      // }

      setOpen(false);
      await fetchSites();
      await refreshPending();
    } catch (err) {
      console.error(err);
      setError("Failed to submit transfer");
    } finally {
      setSubmitting(false);
    }
  };

  const submitSales = async () => {
    const v = validateTransfer();
    if (v) {
      setError(v);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      let items = transferForm.items.filter(
        (item) => item.product_id && item.amount && item.price
      );

      let payload = {
        ...transferForm,
        expected_date: transferForm.expected_date.format("YYYY-MM-DD"),
        user_id: userId,
        items: items.map((item) => ({
          ...item,
          amount: Math.abs(Number(item.amount)),
          price: Math.abs(Number(item.price)),
        })),
      };

      await axios.post("/productSales", payload);

      setOpen(false);
      await fetchSites();
      // await refreshPending();
    } catch (err) {
      console.error(err);
      setError("Failed to submit transfer");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    // dialog
    open,
    openDialog,
    closeDialog,

    // form + state
    transferForm,
    setTransferForm,
    submitting,
    error,
    setError,

    // site options + filters for selects
    siteOptions,
    fromSiteOptionsFiltered,
    toSiteOptionsFiltered,

    // helpers for selects
    getProductCatalogOptions,
    getProductOptions,
    handleSiteChange,

    // items handling
    addTransferItem,
    removeTransferItem,
    updateTransferItem,

    // UI flags
    showToSite,
    showFromSite,
    showRestFields,
    renderDateLabel,
    submitTransfer,
    submitSales
  };
}
